/**
 * A short message in the corner, for what does not warrant a dialog.
 *
 * This existed twice, once in admin/core.js and once in biolink-editor.js, as
 * two nearly identical copies that had drifted in their timings. It is one
 * implementation now, and it brings its own stack when the page has none: the
 * admin layout and the biolink editor render a #toastStack, every other page
 * does not, and a toast that silently does nothing outside those two is a bug
 * waiting for whoever moves a call.
 */

const { SURFACE, el, chip } = require('./style');

// How long a toast stays. Two values, not eight: the call sites used to each
// pick a number between 1.2 and 4 seconds, and the difference between 1400 and
// 1500 was never meant by anyone. What does matter is that something that went
// wrong stays long enough to read.
const VISIBLE_MS = 2800;
const PROBLEM_MS = 4500;
const FADE_MS = 220;

/**
 * The container, made if it is not already in the layout.
 * @returns {HTMLElement}
 */
function stack() {
    let node = document.getElementById('toastStack');
    if (!node) {
        node = document.createElement('div');
        node.id = 'toastStack';
        node.className = 'pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col items-end gap-2';
        document.body.appendChild(node);
    }
    // A live region, so a toast is read out and not only seen. Also set on the
    // stacks the admin layout and the biolink editor render themselves.
    if (!node.hasAttribute('aria-live')) {
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
    }
    return node;
}

// Made when the page loads rather than with the first toast: a live region
// that appears together with its message is not announced, so the first toast
// on a page would otherwise be the one nobody hears.
if (typeof document !== 'undefined') {
    if (document.body) stack();
    else document.addEventListener('DOMContentLoaded', stack);
}

/**
 * @param {string} message
 * @param {string} [kind] - success, error, warning or info
 * @param {number} [duration] - milliseconds, when this one has a reason to
 *   differ. Left out, the kind decides.
 * @param {Object} [action] - { label, run } for one button inside the toast,
 *   an Undo for instance. Pressing it closes the toast and calls `run`. A
 *   toast with an action stays until its duration is up, so pass a duration
 *   long enough to reach the button.
 * @returns {HTMLElement} the toast, with a `close()` method
 */
function toast(message, kind, duration, action) {
    // The same card and the same chip as a dialog, in small. A message used to
    // look like one thing when it arrived as a solid coloured pill and like
    // another when the same words arrived in a dialog.
    const node = el('div', 'pointer-events-auto flex max-w-sm translate-y-2 items-center gap-3 px-3 py-2.5 '
        + 'text-sm text-slate-700 opacity-0 transition-all duration-200 dark:text-slate-200 ' + SURFACE);

    const mark = chip(kind || 'info', 'sm');
    if (mark) node.appendChild(mark);
    // textContent, because a message can carry a file name or a username.
    node.appendChild(el('span', 'min-w-0', message));

    let closed = false;
    let timer = null;
    node.close = function () {
        if (closed) return;
        closed = true;
        clearTimeout(timer);
        node.classList.add('translate-y-2', 'opacity-0');
        setTimeout(function () {
            if (node.parentNode) node.parentNode.removeChild(node);
        }, FADE_MS);
    };

    if (action && action.label) {
        const button = el('button', 'ml-1 shrink-0 rounded-md px-2 py-1 text-sm font-semibold text-blue-700 '
            + 'transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-500/10', action.label);
        button.type = 'button';
        button.addEventListener('click', function () {
            node.close();
            if (typeof action.run === 'function') action.run();
        });
        node.appendChild(button);
    }

    stack().appendChild(node);

    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : fn => setTimeout(fn, 16);
    raf(function () { node.classList.remove('translate-y-2', 'opacity-0'); });

    timer = setTimeout(node.close, duration || (kind === 'error' || kind === 'warning' ? PROBLEM_MS : VISIBLE_MS));

    return node;
}

module.exports = { toast };
